import {
  CDN_DOMAINS,
  getLaunchDarklyClientId,
} from '@hopin-team/config-hopin-constants';
import { AnalyticsProvider } from '@hopin-team/provider-analytics';
import { FeatureFlagProvider } from '@hopin-team/provider-feature-flag';
import { TranslationProvider } from '@hopin-team/provider-translation';
import {
  createReactiveMap,
  SlicesProvider,
} from '@hopin-team/registration-schedule-shared-state';
import React, { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';

import { Loading } from './components/loading/spinner-loading-component';
import { analyticsClient, clientCallback } from './features/analytics/config';
import { ThemeLoader } from './features/branding/theme-loader';
import { OverlayProvider } from './providers/overlay';
import store from './redux/store';
import { Registration } from './Registration';
import { defaultFlags } from './utils/flags';
import { GlobalStyle } from './utils/globalStyle';
import { addCustomTiming } from './utils/logging';
import { getEventSlug } from './utils/url';

declare global {
  interface Window {
    Osano: any;
    Android?: {
      onRegistrationComplete?: (value?: string) => void;
      onRegistrationFailure?: (value?: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        registrationComplete?: WebkitMessageHandler;
        registrationFailure?: WebkitMessageHandler;
      };
    };
  }
}

interface WebkitMessageHandler {
  postMessage: (obj?: Record<string, unknown>) => void;
}

const RegistrationSlice = () => {
  const eventSlug = getEventSlug(window.location);
  const reactiveMap = useMemo(() => createReactiveMap(), []);

  useEffect(() => {
    addCustomTiming('REGISTRATION_SLICE_INIT', Date.now());
  }, []);

  return (
    <>
      <GlobalStyle />
      <FeatureFlagProvider
        loadMethod="async"
        flagScopes={[
          {
            type: 'launchDarkly',
            clientId: getLaunchDarklyClientId(),
            context: {
              kind: 'user',
              key: 'eventSlug',
              custom: { eventSlug },
              eventSlug,
            },
            options: {
              bootstrap: defaultFlags,
            },
          },
        ]}
      >
        <ThemeLoader>
          <TranslationProvider
            getLocale={(locale: string) => import(`./locales/${locale}.json`)}
            fallback={<Loading />}
          >
            <AnalyticsProvider
              client={analyticsClient}
              clientCallback={clientCallback}
            >
              <Provider store={store}>
                <OverlayProvider>
                  <SlicesProvider
                    reactiveMap={reactiveMap}
                    origin={CDN_DOMAINS.production}
                  >
                    <Registration />
                  </SlicesProvider>
                </OverlayProvider>
              </Provider>
            </AnalyticsProvider>
          </TranslationProvider>
        </ThemeLoader>
      </FeatureFlagProvider>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default RegistrationSlice;
